import useNameEntryMode from "../../utils/useNameEntryMode";
import { nameFields, composeIndividualName } from "../../utils/individualName";
import AppLoadingOverlay from "../ui/AppLoadingOverlay";
// AddClientModal.jsx

import { useState, useEffect, useRef } from "react";
import Modal from "../ui/Modal";
import { useAuth } from "../../auth/AuthProvider";
import { ROLES, normalizeRole } from "../../auth/permissions";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import "../ui/css/ConfirmDialog.css";
import CountrySelect from "../ui/CountrySelect";
import DatePickerField from "../ui/DatePickerField";
import ClientPhoneField from "./ClientPhoneField";
import IndividualNamePreview from "./IndividualNamePreview";
import uploadIcon from "../../images/upload-icon-1.svg";
import Lottie from "lottie-react";
import individualIcon from "../../images/individual-lottie.json";
import entityIcon from "../../images/building-lottie.json";
import activeClientIcon from "../../images/new-client-active.json";
import formerClientIcon from "../../images/new-client-former.json";
import prospectClientIcon from "../../images/new-client-prospect.json";
import "./AddClientModal.css";
import useClientDocumentIntake from "./useClientDocumentIntake";
import ClientDocumentIntakeReview from "./ClientDocumentIntakeReview";
import ClientIntakeDocuments from "./ClientIntakeDocuments";
import DocumentPreviewModal from "../ui/DocumentPreviewModal";
import PdfDocumentPreview from "../ui/PdfDocumentPreview";
import { categoryLabels, cvSourceLabels, intakeLabels } from "../../utils/clientDocumentIntake";
import { formatManualClientName } from "../../utils/clientName";


function readMapboxToken() {
  const raw =
    process.env.REACT_APP_MAPBOX_TOKEN ||
    process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
    "";

  return String(raw).trim().replace(/^["']|["']$/g, "");
}

const MAPBOX_TOKEN = readMapboxToken();

function capitalizeFirstLetter(value) {
  return String(value || "").replace(/^(\s*)([a-z])/, (_, spaces, letter) => (
    `${spaces}${letter.toUpperCase()}`
  ));
}

function getContextEntry(feature, prefix) {
  const list = Array.isArray(feature?.context) ? feature.context : [];
  return list.find((item) => String(item?.id || "").startsWith(`${prefix}.`));
}

function toCountryCode(value) {
  const raw = String(value || "")
    .trim()
    .toUpperCase();
  if (!raw) return "";
  if (raw.length === 2) return raw;
  return raw.split("-").pop() || raw;
}

function parseMapboxFeature(feature) {
  if (!feature) return null;

  const countryCtx = getContextEntry(feature, "country");
  const placeCtx =
    getContextEntry(feature, "place") ||
    getContextEntry(feature, "locality") ||
    getContextEntry(feature, "district");

  const houseNumber = feature?.address ? `${feature.address} ` : "";
  const streetName =
    feature?.text ||
    feature?.properties?.address ||
    feature?.place_name?.split(",")?.[0] ||
    "";
  const firstLine = `${houseNumber}${streetName}`.trim() || feature?.place_name || "";

  return {
    id: String(feature.id || firstLine),
    label: feature.place_name || firstLine,
    address: firstLine,
    city: placeCtx?.text || "",
    country: toCountryCode(countryCtx?.short_code || countryCtx?.text || ""),
  };
}

function parseMapboxCityFeature(feature) {
  if (!feature) return null;

  const countryCtx = getContextEntry(feature, "country");
  const cityName =
    feature?.text ||
    feature?.place_name?.split(",")?.[0] ||
    feature?.properties?.name ||
    "";

  if (!cityName) return null;

  return {
    id: String(feature.id || cityName),
    city: cityName,
    country: toCountryCode(countryCtx?.short_code || countryCtx?.text || ""),
    label: feature.place_name || cityName,
  };
}

function ClientAddressTypeahead({
  kind = "address",
  documentFilled = false,
  value,
  placeholder,
  suggestions,
  loading,
  dropdownOpen,
  onChange,
  onCommit,
  onSuggestionSelect,
}) {
  const isAddress = kind === "address";
  const rawValue = String(value || "").trim();
  const customOption =
    rawValue
      ? {
          id: `__custom_${kind}__`,
          label: rawValue,
          ...(isAddress ? { address: rawValue } : { city: rawValue }),
          isCustomValue: true,
        }
      : null;
  const displaySuggestions = customOption
    ? [
        customOption,
        ...suggestions.filter((item) => {
          const itemPrimaryValue = String(
            isAddress ? item.address || item.label || "" : item.city || item.label || ""
          ).trim();
          const itemLabelValue = String(item.label || itemPrimaryValue).trim();
          const rawLower = rawValue.toLowerCase();
          return !(
            itemPrimaryValue.toLowerCase() === rawLower &&
            itemLabelValue.toLowerCase() === rawLower
          );
        }),
      ]
    : suggestions;
  const shouldShowDropdown = dropdownOpen && displaySuggestions.length > 0;

  return (
    <div className="clientAddressSuggestBox">
      <input
        className="form-control addClientLocationInput"
        aria-label={isAddress ? "Address" : "City"}
        data-document-filled={documentFilled || undefined}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" || e.nativeEvent?.isComposing) return;
          e.preventDefault();
          onCommit?.();
        }}
        onFocus={() => {
          if (suggestions.length > 0) {
            // Suggestions are reopened by the next input event; keep focus behavior quiet.
          }
        }}
        onBlur={() => {
          window.setTimeout(() => {
            onCommit?.();
          }, 120);
        }}
        placeholder={placeholder}
      />

      {loading ? (
        <div className="clientAddressSuggestStatus">
          Searching {isAddress ? "address" : "city"}...
        </div>
      ) : null}

      {shouldShowDropdown ? (
        <div className="clientAddressSuggestDropdown">
          {displaySuggestions.map((item) => (
            <button
              key={item.id || item.label || item.city}
              type="button"
              className={`clientAddressSuggestItem${
                item.isCustomValue ? " clientAddressSuggestItemCustom" : ""
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (item.isCustomValue) {
                  onCommit?.();
                  return;
                }
                onSuggestionSelect?.(item);
              }}
            >
              <span className="clientAddressSuggestPrimary">
                {isAddress ? item.address || item.label : item.city || item.label}
              </span>
              <span className="clientAddressSuggestSecondary">
                {item.isCustomValue
                  ? `Use entered ${isAddress ? "address" : "city"}`
                  : item.label}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// -------------------------------------------------------
// -----------------   EXPORT FUNCTION   -----------------
// -------------------------------------------------------

function toDateOrNull(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate(); // Firestore Timestamp
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateKey(v) {
  const d = toDateOrNull(v);
  return d ? d.getTime() : null;
}

function cleanString(v, trim = false) {
  const text = String(v || "");
  return trim ? text.trim() : text;
}

function normalizeClientForCompare(value) {
  const clientType = value?.clientType || null;
  const status = value?.status || null;
  const isEntity = clientType === "entity";
  const isClient = status === "client";
  const isFormerClient = status === "former client";

  return {
    clientName: cleanString(value?.clientName, true),
    ...nameFields(value),
    clientType,
    status,
    address: cleanString(value?.address),
    city: cleanString(value?.city, true),
    country: value?.country || "",
    contactName: isEntity ? cleanString(value?.contactName, true) : "",
    contactEmail: cleanString(value?.contactEmail, true),
    contactPhone: cleanString(value?.contactPhone, true),
    joinDate: isClient || isFormerClient ? dateKey(value?.joinDate) : null,
    leaveDate: isFormerClient ? dateKey(value?.leaveDate) : null,
  };
}


export default function AddClientModal({
  open,
  onClose,
  onSubmit,
  preferredPhoneCountry = "",
  initialFiles = null,
  initialIntake = null,
  existingKyc = {},
  existingCv = {},
  client = null,
  mode = "add",
}) {
  const isEditing = mode === "edit";
  // Client status mirrors the KYC outcome (the final-overview flow moves it):
  // Staff front sets it freely at creation but cannot change it afterwards —
  // same boundary the Firestore rules enforce, surfaced here so the save
  // cannot fail on a locked field.
  const authCtx = useAuth();
  const { mode: individualNameMode, ready: nameModeReady } = useNameEntryMode(client?.orgId || authCtx?.me?.orgId);
  const statusLocked =
    isEditing && normalizeRole(authCtx?.role) === ROLES.STAFF_FRONT;
  const calRef = useRef(null);
  const modalCardRef = useRef(null);
  const [documentDragActive, setDocumentDragActive] = useState(false);
  const addressAbortRef = useRef(null);
  const cityAbortRef = useRef(null);
  const addressTimerRef = useRef(null);
  const cityTimerRef = useRef(null);

  const [draft, setDraft] = useState({
    clientName: "",
    clientType: null,
    status: "client",

    // ✅ NEW FIELD (stored in DB)
    // nationalityOrIncorporationCountry: "",

    address: "",
    city: "",
    country: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    joinDate: new Date(),
    leaveDate: null,
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);

  const intake = useClientDocumentIntake({ open, draft, setDraft, existingKyc, reviewExisting: isEditing, separateNames: individualNameMode === "separate" });
  const documentFilled = field => {
    const value = intake.kyc[field]?.value ?? draft[field];
    return isEditing && !!value && Object.prototype.hasOwnProperty.call(intake.filledValues, field)
      && value === intake.filledValues[field];
  };
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const saveAttempt = useRef(null);
  const initialFilesHandled = useRef(null);
  const intakeRef = useRef(intake);
  intakeRef.current = intake;
  const savingRef = useRef(false);
  useEffect(() => { if (open) { setAttachmentPreview(null); saveAttempt.current = null; setSaving(false); savingRef.current = false; setSaveError(""); } }, [open]);
  const closeModal = () => { if (!savingRef.current && !attachmentPreview && !intake.reviewOpen && !intake.unidentified) onClose?.(); };

  useEffect(() => {
    setDocumentDragActive(false);
    if (!open) return undefined;
    const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
    const inside = (target) => target instanceof Node && modalCardRef.current?.contains(target);
    const reset = () => setDocumentDragActive(false);
    const onDragOver = (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      const allowed = inside(e.target) && !savingRef.current && !saveAttempt.current && !intakeRef.current.busy && !intakeRef.current.review;
      e.dataTransfer.dropEffect = allowed ? "copy" : "none";
      setDocumentDragActive(allowed);
    };
    const onDrop = (e) => {
      reset();
      if (!isFileDrag(e) && !e.dataTransfer?.files?.length) return;
      e.preventDefault();
      if (!inside(e.target)) return;
      const files = Array.from(e.dataTransfer?.files || []);
      if (!files.length) return;
      if (savingRef.current || saveAttempt.current || intakeRef.current.busy || intakeRef.current.review) return;
      intakeRef.current.run(files);
    };
    const onDragLeave = (e) => { if (!e.relatedTarget) reset(); };
    const onKeyDown = (e) => { if (e.key === "Escape") reset(); };
    document.addEventListener("dragover", onDragOver, true);
    document.addEventListener("drop", onDrop, true);
    document.addEventListener("dragleave", onDragLeave, true);
    document.addEventListener("dragend", reset, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("blur", reset);
    return () => {
      document.removeEventListener("dragover", onDragOver, true);
      document.removeEventListener("drop", onDrop, true);
      document.removeEventListener("dragleave", onDragLeave, true);
      document.removeEventListener("dragend", reset, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("blur", reset);
    };
  }, [open]);


  const isIndividual = draft.clientType === "individual";
  const isEntity = draft.clientType === "entity";
  const categorySelected = isIndividual || isEntity;
  const readyDocuments = intake.documents.filter(document => document.ready);
  const clientFieldsChanged = JSON.stringify(normalizeClientForCompare(draft)) !== JSON.stringify(normalizeClientForCompare(client || {}));
  const kycFieldsChanged = Object.entries(intake.kyc).some(([field, item]) => item.value !== (existingKyc[field] || ''));
  const cvFieldsChanged = intake.cv && (intake.cv.businessActivity.trim() !== (existingCv.businessActivity || '').trim()
    || intake.cv.selectedSources.some(source => !(existingCv.sourceOfWealth || []).includes(source)));
  // Wait for the hook to expose a newly accepted CV before deciding the outcome.
  const cvPending = readyDocuments.some(document => document.extraction?.cvProfile) && !intake.cv;
  const fieldsChanged = !isEditing || clientFieldsChanged || kycFieldsChanged || cvFieldsChanged || cvPending;
  const documentsOnly = isEditing && !fieldsChanged && readyDocuments.length > 0
    && !intake.busy && !intake.review && !intake.hasUnresolved && !intake.unidentified;
  const hasChanges = fieldsChanged || readyDocuments.length > 0;
  const canSave = hasChanges && draft.clientName.trim() && draft.clientType && !intake.busy && !intake.review && !intake.hasUnresolved && !intake.unidentified && !saving;

  const showContactName = isEntity; // only entity has "contact name"
  const showContactRow = isIndividual || isEntity; // both have email+phone

  const isProspect = draft.status === "prospect";
  const isClient = draft.status === "client";
  const isFormerClient = draft.status === "former client";

  const showDates = !isProspect; // client + former client
  const showLeaveDate = isFormerClient; // only former client

  const showContactDivider = showContactName || showContactRow;
  const showDatesDivider = isClient || isFormerClient;

  const onTypeChange = (val) => {
    setDraft((p) => {
      const next = { ...p, clientType: val };
      if (val === "individual") next.contactName = ""; // only entity uses it

      // ✅ optional safety: clear the nationality/incorp selection when switching type
      // next.nationalityOrIncorporationCountry = "";

      return next;
    });
  };

  const onStatusChange = (val) => {
    if (statusLocked) return;
    setDraft((p) => {
      const next = { ...p, status: val };
      if (val === "prospect") {
        next.joinDate = null;
        next.leaveDate = null;
      }
      if (val === "client") {
        next.joinDate = p.joinDate || new Date();
        next.leaveDate = null;
      }
      return next;
    });
  };

  // reset fields when opening
  useEffect(() => {
    if (!open) return;
    setDraft({
      clientName: isEditing ? client?.clientName || "" : "",
      ...nameFields(isEditing ? client : {}),
      clientType: isEditing ? client?.clientType || null : null,
      status: isEditing ? client?.status || null : "client",
      address: isEditing ? client?.address || "" : "",
      city: isEditing ? client?.city || "" : "",
      country: isEditing ? client?.country || "" : "",
      contactName: isEditing ? client?.contactName || "" : "",
      contactEmail: isEditing ? client?.contactEmail || "" : "",
      contactPhone: isEditing ? client?.contactPhone || "" : "",
      joinDate: isEditing ? toDateOrNull(client?.joinDate) : new Date(),
      leaveDate: isEditing ? toDateOrNull(client?.leaveDate) : null,
    });
    setAddressSuggestions([]);
    setCitySuggestions([]);
    setAddressLoading(false);
    setCityLoading(false);
    setAddressDropdownOpen(false);
    setCityDropdownOpen(false);
  }, [open, client, isEditing]);

  // Start table-dropped files after the modal's draft/intake reset effects.
  // Deferring also avoids processing twice during StrictMode effect replay.
  useEffect(() => {
    if (!open) { initialFilesHandled.current = null; return undefined; }
    if (!initialFiles?.length && !initialIntake) return undefined;
    const incoming = initialIntake || initialFiles;
    let active = true;
    queueMicrotask(() => {
      if (!active || initialFilesHandled.current === incoming) return;
      initialFilesHandled.current = incoming;
      if (initialIntake) intakeRef.current.applyExtraction(initialIntake);
      else intakeRef.current.run(initialFiles);
    });
    return () => { active = false; };
  }, [open, initialFiles, initialIntake]);

  useEffect(() => {
    if (open) return undefined;

    clearTimeout(addressTimerRef.current);
    clearTimeout(cityTimerRef.current);
    addressAbortRef.current?.abort();
    cityAbortRef.current?.abort();
    addressTimerRef.current = null;
    cityTimerRef.current = null;
    addressAbortRef.current = null;
    cityAbortRef.current = null;

    return undefined;
  }, [open]);

  useEffect(() => {
    return () => {
      clearTimeout(addressTimerRef.current);
      clearTimeout(cityTimerRef.current);
      addressAbortRef.current?.abort();
      cityAbortRef.current?.abort();
    };
  }, []);

  function applyAddressSuggestion(item) {
    setDraft((p) => ({
      ...p,
      address: capitalizeFirstLetter(item.address || ""),
      city: item.city ? capitalizeFirstLetter(item.city) : p.city,
      country: item.country || p.country,
    }));
    setAddressSuggestions([]);
    setAddressDropdownOpen(false);
  }

  function applyCitySuggestion(item) {
    setDraft((p) => ({
      ...p,
      city: capitalizeFirstLetter(item.city || ""),
      country: item.country || p.country,
    }));
    setCitySuggestions([]);
    setCityDropdownOpen(false);
  }

  async function fetchAddressSuggestions(query) {
    if (!MAPBOX_TOKEN) {
      setAddressSuggestions([]);
      setAddressLoading(false);
      return;
    }

    const trimmed = String(query || "").trim();
    if (trimmed.length < 3) {
      setAddressSuggestions([]);
      setAddressLoading(false);
      return;
    }

    addressAbortRef.current?.abort();

    const controller = new AbortController();
    addressAbortRef.current = controller;
    setAddressLoading(true);

    try {
      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          trimmed
        )}.json` +
        `?access_token=${encodeURIComponent(MAPBOX_TOKEN)}` +
        `&autocomplete=true&types=address,place,locality,neighborhood`;

      const res = await fetch(url, { signal: controller.signal });
      const json = await res.json().catch(() => ({}));
      const items = Array.isArray(json?.features)
        ? json.features.map(parseMapboxFeature).filter(Boolean)
        : [];
      setAddressSuggestions(items);
      setAddressDropdownOpen(items.length > 0);
    } catch (err) {
      if (err?.name !== "AbortError") {
        setAddressSuggestions([]);
        setAddressDropdownOpen(false);
      }
    } finally {
      if (addressAbortRef.current === controller) {
        addressAbortRef.current = null;
      }
      setAddressLoading(false);
    }
  }

  async function fetchCitySuggestions(query) {
    if (!MAPBOX_TOKEN) {
      setCitySuggestions([]);
      setCityLoading(false);
      return;
    }

    const trimmed = String(query || "").trim();
    if (trimmed.length < 2) {
      setCitySuggestions([]);
      setCityLoading(false);
      return;
    }

    cityAbortRef.current?.abort();

    const controller = new AbortController();
    cityAbortRef.current = controller;
    setCityLoading(true);

    try {
      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          trimmed
        )}.json` +
        `?access_token=${encodeURIComponent(MAPBOX_TOKEN)}` +
        `&autocomplete=true&types=place,locality,district`;

      const res = await fetch(url, { signal: controller.signal });
      const json = await res.json().catch(() => ({}));
      const items = Array.isArray(json?.features)
        ? json.features.map(parseMapboxCityFeature).filter(Boolean)
        : [];
      setCitySuggestions(items);
      setCityDropdownOpen(items.length > 0);
    } catch (err) {
      if (err?.name !== "AbortError") {
        setCitySuggestions([]);
        setCityDropdownOpen(false);
      }
    } finally {
      if (cityAbortRef.current === controller) {
        cityAbortRef.current = null;
      }
      setCityLoading(false);
    }
  }

  function handleAddressInput(value) {
    setDraft((p) => ({ ...p, address: capitalizeFirstLetter(value) }));

    clearTimeout(addressTimerRef.current);

    const trimmed = String(value || "").trim();
    if (!MAPBOX_TOKEN || trimmed.length < 3) {
      setAddressSuggestions([]);
      setAddressDropdownOpen(false);
      setAddressLoading(false);
      return;
    }

    addressTimerRef.current = setTimeout(() => {
      fetchAddressSuggestions(trimmed);
    }, 280);
  }

  function handleCityInput(value) {
    setDraft((p) => ({ ...p, city: capitalizeFirstLetter(value) }));

    clearTimeout(cityTimerRef.current);

    const trimmed = String(value || "").trim();
    if (!MAPBOX_TOKEN || trimmed.length < 2) {
      setCitySuggestions([]);
      setCityDropdownOpen(false);
      setCityLoading(false);
      return;
    }

    cityTimerRef.current = setTimeout(() => {
      fetchCitySuggestions(trimmed);
    }, 240);
  }

  function commitCustomAddress() {
    clearTimeout(addressTimerRef.current);
    addressTimerRef.current = null;
    addressAbortRef.current?.abort();
    addressAbortRef.current = null;
    setAddressLoading(false);
    setAddressSuggestions([]);
    setAddressDropdownOpen(false);
  }

  function commitCustomCity() {
    clearTimeout(cityTimerRef.current);
    cityTimerRef.current = null;
    cityAbortRef.current?.abort();
    cityAbortRef.current = null;
    setCityLoading(false);
    setCitySuggestions([]);
    setCityDropdownOpen(false);
  }

  async function save(e) {
    e.preventDefault();
    if (!canSave || savingRef.current) return;
    if (!nameModeReady) { setSaveError("Organisation settings are still loading. Please try again."); return; }
    const incompatible = intake.documents.some(d => d.ready && (
      (["id_document", "cv"].includes(d.category) && draft.clientType !== "individual") ||
      (d.category === "trade_licence" && draft.clientType !== "entity")));
    if (incompatible) {
      setSaveError("The document type does not match the selected client type. Check the client type or remove documents that do not belong to this client.");
      return;
    }
    const name = isIndividual && individualNameMode === "separate"
      ? composeIndividualName(draft) || formatManualClientName(draft.clientName)
      : formatManualClientName(draft.clientName);
    const payload = {
      clientName: name,
      ...nameFields(isIndividual && individualNameMode === "separate" ? draft : {}),
      nameEntryMode: individualNameMode,
      clientType: draft.clientType,
      status: draft.status,

      // ✅ NEW FIELD saved to DB
      // nationalityOrIncorporationCountry:
      //   draft.nationalityOrIncorporationCountry || null,

      address: draft.address,
      city: draft.city.trim(),
      country: draft.country,

      contactName: isEntity ? draft.contactName.trim() || null : null,
      contactEmail: draft.contactEmail.trim() || null,
      contactPhone: draft.contactPhone.trim() || null,

      joinDate: isClient || isFormerClient ? draft.joinDate ?? null : null,
      leaveDate: isFormerClient ? draft.leaveDate ?? null : null,
      documentsOnly,
      intakeFiles: intake.filesForSave(),
      intakeKyc: intake.kycForSave(),
      intakeCv: intake.cvForSave(),
    };
    if (!saveAttempt.current) saveAttempt.current = { id: isEditing ? client.id : crypto.randomUUID(), payload, clientSaved: false };
    savingRef.current = true; setSaving(true); setSaveError("");
    try {
      await onSubmit?.({ ...saveAttempt.current.payload, intakeSession: saveAttempt.current });
      onClose?.();
    } catch (error) {
      setSaveError(saveAttempt.current.payload.documentsOnly
        ? `The documents could not be saved. Please retry. ${error.message}`
        : saveAttempt.current.clientSaved
        ? `The client was ${isEditing ? "updated" : "created"}, but its documents could not be saved. Retry Save to attach them. ${error.message}`
        : `Save could not be confirmed. Retry Save with the same details. ${error.message}`);
    } finally { savingRef.current = false; setSaving(false); }
  }

  useEffect(() => {
    if (!open || !attachmentPreview) return undefined;
    const closePreview = event => {
      if (event.key === "Escape") { event.stopPropagation(); setAttachmentPreview(null); }
    };
    window.addEventListener("keydown", closePreview, true);
    return () => window.removeEventListener("keydown", closePreview, true);
  }, [open, attachmentPreview]);

  // Client type buttons and status options
  const typeOptions = [
    { label: "Individual", value: "individual", icon: individualIcon },
    { label: "Entity", value: "entity", icon: entityIcon },
  ];

  const statusOptions = [
    { label: "Client", value: "client", icon: activeClientIcon },
    { label: "Prospect", value: "prospect", icon: prospectClientIcon },
    { label: "Former client", value: "former client", icon: formerClientIcon },
  ];

  // const natLabel = isEntity ? "Incorporation country" : "Nationality";

  return (
    <>
    <Modal
      open={open && !attachmentPreview}
      title={documentsOnly ? "Attached documents" : isEditing ? "Edit Client" : "Add Client"}
      onClose={closeModal}
      className={`addClientModal${isEditing ? " editClientModal" : ""}${documentsOnly ? " clientDocumentsOnlyModal" : ""}`}
      cardRef={modalCardRef}
      overlay={intake.busy ? (
        <div className="addClientIntakeLoading" role="status" aria-live="polite" aria-label={intake.message}>
          <AppLoadingOverlay title={intake.message} loaderStyle="dots" bg="transparent" />
        </div>
      ) : documentDragActive ? (
        <div className="addClientDocumentDropOverlay" aria-hidden="true">
          <div className="addClientDocumentDropContent">
            <span className="addClientDocumentDropIcon" style={{ "--upload-icon": `url("${uploadIcon}")` }} aria-hidden="true" />
            <span>Drop an ID, proof of address, trade licence or CV</span>
          </div>
        </div>
      ) : null}
      footer={
        <>
          <button
            type="submit"
            form="addClientForm"
            className={`btn btn-sm btn-modal-save btn-w3 ${
              canSave ? "" : "btn-modal-save--blocked"
            }`}
            disabled={!canSave}
          >
            {saving ? "Saving…" : saveAttempt.current ? "Retry Save" : documentsOnly ? (readyDocuments.length === 1 ? "Save document" : "Save documents") : "Save"}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-modal-cancel btn-w1"
            onClick={closeModal}
            disabled={saving}
          >
            Cancel
          </button>
        </>
      }
    >
      <form id="addClientForm" onSubmit={save}>
        {saveError && <div className="addClientDocumentDropMessage" role="alert">{saveError}</div>}
        {!intake.busy && intake.message && <div className="addClientDocumentDropMessage" role="status">{intake.message}</div>}
        {intake.documents.some(d => !d.ready && d.status !== 'extracted') && <ul className="clientIntakeFiles">
          {intake.documents.filter(d => !d.ready && d.status !== 'extracted').map(d => <li key={d.id}>
            <span><small>{categoryLabels[d.category] || "Not identified"} · {d.error?.message || "Review required"}</small></span>
            {d.status !== 'extracted' && <button type="button" className="btn btn-sm" disabled={intake.busy || !!saveAttempt.current} onClick={() => intake.run([d.file], true)}>Retry</button>}
            <button type="button" className="btn btn-sm" aria-label={`Remove ${categoryLabels[d.category] || "document"}`} disabled={intake.busy || !!saveAttempt.current} onClick={() => intake.remove(d.id)}>Remove</button>
          </li>)}
        </ul>}
        {intake.review && !intake.reviewOpen && <div className="intakeReviewPending" role="status">
          <span><strong>Details to check</strong><small>{intake.review.review.map(row => intakeLabels[row.field].replace(' (KYC)', '')).join(', ')}</small></span>
          <button type="button" className="btn btn-sm" onClick={intake.openReview}>Review details</button>
        </div>}
        {!documentsOnly && <fieldset disabled={saving || intake.busy || !!saveAttempt.current || intake.reviewOpen} inert={saving || intake.busy || !!saveAttempt.current || intake.reviewOpen ? true : undefined}>

        <div className="row g-3">
          <div className="col-12">
          <div className="addClientChoicesLayout">
          <div className="addClientTypeGroup">
            <label className="form-label addClientChoiceHeading">Type</label>
            <div className="addClientTypeButtons" role="group" aria-label="Client type">
              {typeOptions.map(option => <button
                key={option.value}
                type="button"
                className="addClientTypeButton"
                aria-label={option.label}
                aria-pressed={draft.clientType === option.value}
                title={option.label}
                onClick={() => onTypeChange(option.value)}
              >
                <span aria-hidden="true"><Lottie animationData={option.icon} loop autoplay className="addClientTypeIcon" /></span>
                <span className="addClientTypeLabel" data-document-filled={documentFilled("clientType") && draft.clientType === option.value || undefined}>{option.label}</span>
              </button>)}
            </div>
          </div>

          <div className="addClientChoicesDivider" aria-hidden="true" />

          <fieldset
            className="addClientStatusGroup addClientCategoryGated"
            disabled={!categorySelected}
            inert={!categorySelected ? true : undefined}
          >
            <label className="form-label addClientChoiceHeading">Status</label>
            <div className="addClientTypeButtons addClientStatusButtons" role="group" aria-label="Client status">
              {statusOptions.map(option => <button
                key={option.value}
                type="button"
                className="addClientTypeButton"
                aria-label={option.label}
                aria-pressed={draft.status === option.value}
                title={statusLocked ? "Client status is changed by Staff compliance" : option.label}
                disabled={statusLocked && draft.status !== option.value}
                onClick={() => onStatusChange(option.value)}
              >
                <span aria-hidden="true"><Lottie animationData={option.icon} loop autoplay className="addClientTypeIcon" /></span>
                <span className="addClientTypeLabel">{option.label}</span>
              </button>)}
            </div>
          </fieldset>

          </div>
          </div>
        </div>

        <fieldset
          className="row g-3 addClientCategoryGated addClientDetailsFields"
          disabled={!categorySelected}
          inert={!categorySelected ? true : undefined}
        >
        <div className="contactDivider" style={{ margin: "25px 0 0px" }} />

        <div className="col-12">
          <div className="addClientNameField">
          <label className="form-label addClientNameLabel" htmlFor="add-client-name">Client name</label>
          {isIndividual && individualNameMode === "separate" ? (
            <IndividualNamePreview
              id="add-client-name"
              inputClass="form-control addClientNameInput"
              displayName={draft.clientName}
              value={draft}
              onChange={(patch) => setDraft(p => ({ ...p, ...patch, clientName: patch.name }))}
            />
          ) : (
          <input
            id="add-client-name"
            data-document-filled={documentFilled("clientName") || undefined}
            className="form-control addClientNameInput"
            value={draft.clientName}
            onChange={(e) =>
              setDraft((p) => ({
                ...p,
                clientName: e.target.value,
              }))
            }
            onBlur={() => setDraft((p) => ({ ...p, clientName: formatManualClientName(p.clientName) }))}
            placeholder={isIndividual ? "e.g. John Smith" : "e.g. ACME LLC"}
          />
          )}
          </div>
        </div>

          {isEntity && intake.kyc.incorporationDate && <div className="col-12 col-sm-6" data-intake-field="incorporationDate" data-document-filled={documentFilled("incorporationDate") || undefined}>
            <label className="form-label">Date of incorporation</label>
            <DatePickerField
              value={intake.kyc.incorporationDate.value ? new Date(`${intake.kyc.incorporationDate.value}T00:00:00`) : null}
              onChange={date => intake.editKyc("incorporationDate", date && !Number.isNaN(date.getTime()) ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}` : "")}
              maxDate={new Date()} appendTo="body" placeholder="Select date" stagedView
            />
          </div>}
          {isIndividual && (intake.kyc.dateOfBirth || intake.kyc.nationality) && <>
            {intake.kyc.dateOfBirth && <div className="col-12 col-sm-6" data-intake-field="dateOfBirth" data-document-filled={documentFilled("dateOfBirth") || undefined}>
              <DatePickerField
                label="Date of birth"
                className="addClientNameField addClientJoinDate h-100"
                hideTriggerButton
                value={intake.kyc.dateOfBirth.value ? new Date(`${intake.kyc.dateOfBirth.value}T00:00:00`) : null}
                onChange={date => intake.editKyc("dateOfBirth", date && !Number.isNaN(date.getTime()) ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}` : "")}
                maxDate={new Date()} appendTo="body" placeholder="Select date" stagedView
              />
            </div>}
            {intake.kyc.nationality && <div className="col-12 col-sm-6" data-intake-field="nationality" data-document-filled={documentFilled("nationality") || undefined}>
              <div className="addClientNameField h-100">
                <label className="form-label addClientNameLabel" htmlFor="add-client-nationality">Nationality</label>
                <CountrySelect value={intake.kyc.nationality.value} onChange={value => intake.editKyc("nationality", value || "")} inputId="add-client-nationality" className="w-100 addClientCountrySelect" optionFontSize={13} optionTextClassName="clientModalCountryOptionText" />
              </div>
            </div>}
          </>}

          {/* ------- */}
          <div
            className="contactDivider contactDividerAddress"
            style={{ margin: "25px 0 0px" }}
          />
          {/* ------- */}

          <div className="col-12">
            <div className="addClientNameField addClientAddressCard">
              <label className="form-label addClientNameLabel">Address</label>
              <ClientAddressTypeahead
                kind="address"
                documentFilled={documentFilled("address")}
                value={draft.address}
                onChange={handleAddressInput}
                onCommit={commitCustomAddress}
                suggestions={addressSuggestions}
                loading={addressLoading}
                dropdownOpen={addressDropdownOpen}
                onSuggestionSelect={applyAddressSuggestion}
                placeholder="Address"
              />
              <div className="row g-3 align-items-end mt-0">
                <div className="col-6">
                  <ClientAddressTypeahead
                    kind="city"
                documentFilled={documentFilled("city")}
                    value={draft.city}
                    onChange={handleCityInput}
                    onCommit={commitCustomCity}
                    suggestions={citySuggestions}
                    loading={cityLoading}
                    dropdownOpen={cityDropdownOpen}
                    onSuggestionSelect={applyCitySuggestion}
                    placeholder="City"
                  />
                </div>
                <div className="col-6" data-document-filled={documentFilled("country") || undefined}>
                  <label className="form-label addClientNameLabel" htmlFor="add-client-country">Country</label>
                  <CountrySelect
                    value={draft.country}
                    onChange={(val) => setDraft((p) => ({ ...p, country: val }))}
                    inputId="add-client-country"
                    className="w-100 addClientCountrySelect"
                    optionFontSize={13}
                    optionTextClassName="clientModalCountryOptionText"
                  />
                </div>
              </div>
            </div>
          </div>

          {showContactDivider && (
            <>
              <div
                className="contactDivider contactDividerContact"
                style={{ margin: "25px 0 4px" }}
              />
            </>
          )}

          {(showContactName || showContactRow) && (
            <div className="col-12">
            <div className="addClientNameField addClientContactCard">
            <div className="row g-3">
              {showContactName && (
                <div className="col-12">
                  <label className="form-label addClientNameLabel">Contact name</label>
                  <input
                    className="form-control addClientContactInput"
                    value={draft.contactName || ""}
                    onChange={(e) =>
                      setDraft((p) => ({
                        ...p,
                        contactName: capitalizeFirstLetter(e.target.value),
                      }))
                    }
                    placeholder="e.g. John Smith"
                  />
                </div>
              )}

              {showContactRow && (
                <>
                  <div className="col-6 col-sm-6">
                    <label className="form-label addClientNameLabel">{isIndividual ? "Email" : "Contact email"}</label>
                    <input
                      type="email"
                      data-document-filled={documentFilled("contactEmail") || undefined}
                      className="form-control addClientContactInput"
                      value={draft.contactEmail || ""}
                      onChange={(e) =>
                        setDraft((p) => ({
                          ...p,
                          contactEmail: e.target.value,
                        }))
                      }
                      placeholder="e.g. name@company.com"
                    />
                  </div>

                  <div className="col-6 col-sm-6" data-document-filled={documentFilled("contactPhone") || undefined}>
                    <label className="form-label addClientNameLabel">{isIndividual ? "Phone" : "Contact phone"}</label>
                    <ClientPhoneField
                      preferredCountries={[draft.country, preferredPhoneCountry]}
                      initialValue={draft.contactPhone}
                      resetKey={`${open}:${intake.phoneRevision}`}
                      onChange={(value) =>
                        setDraft((p) => ({
                          ...p,
                          contactPhone: value || "",
                        }))
                      }
                    />
                  </div>
                </>
              )}
            </div>
            </div>
            </div>
          )}

          {isIndividual && intake.cv && (
            <>
              <div className="contactDivider" style={{ margin: "25px 0 4px" }} />
              <div className="col-12">
                <div className="addClientNameField addClientCvActivity">
                  <label className="form-label addClientNameLabel" htmlFor="add-client-activity">Business activity</label>
                  <textarea data-document-filled={isEditing || undefined} id="add-client-activity" className="form-control addClientContactInput"
                    rows={Math.min(14, Math.max(5, Math.ceil(intake.cv.businessActivity.length / 85)))}
                    value={intake.cv.businessActivity}
                    onChange={event => intake.editCvActivity(event.target.value)} />
                </div>
              </div>
              {intake.cv.suggestions.length > 0 && <div className="col-12">
                <div className="addClientNameField addClientCvSources">
                  <div className="form-label addClientNameLabel">Suggested sources of wealth</div>
                  {intake.cv.suggestions.map(suggestion => <div className="addClientCvSuggestion" key={suggestion.category}>
                    <button type="button" className="addClientCvSourceBadge"
                      aria-pressed={intake.cv.selectedSources.includes(suggestion.category)}
                      onClick={() => intake.toggleCvSource(suggestion.category)}>
                      {intake.cv.selectedSources.includes(suggestion.category) && <i className="bi bi-check2 me-1" aria-hidden="true" />}
                      {cvSourceLabels[suggestion.category]}
                    </button>
                    <span>{suggestion.explanation}</span>
                  </div>)}
                </div>
              </div>}
            </>
          )}

          {/* ------- */}
          {showDatesDivider && (
            <>
              <div
                className="contactDivider contactDividerDates"
                style={{ margin: "30px 0 4px" }}
              />
            </>
          )}

          {showDates && (
            <>
              {isClient && (
                <div className="col-12">
                  <DatePickerField
                    label="Join date"
                    className="addClientNameField addClientJoinDate"
                    placeholder="Select date"
                    hideTriggerButton
                    stagedView
                    value={draft.joinDate}
                    onChange={(val) =>
                      setDraft((p) => ({ ...p, joinDate: val }))
                    }
                    dateFormat="d M yy"
                    appendTo="body"
                  />
                </div>
              )}

              {isFormerClient && (
                <>
                  <div className="col-6 col-sm-6">
                    <DatePickerField
                      label="Join date"
                    className="addClientNameField addClientJoinDate"
                    placeholder="Select date"
                    hideTriggerButton
                    stagedView
                      value={draft.joinDate}
                      onChange={(val) =>
                        setDraft((p) => ({ ...p, joinDate: val }))
                      }
                      dateFormat="d M yy"
                      appendTo="body"
                    />
                  </div>

                  <div className="col-6 col-sm-6">
                    <DatePickerField
                      label="Leave date"
                      value={draft.leaveDate}
                      onChange={(val) =>
                        setDraft((p) => ({ ...p, leaveDate: val }))
                      }
                      dateFormat="d M yy"
                      appendTo="body"
                    />
                  </div>
                </>
              )}
            </>
          )}
        </fieldset>
      </fieldset>}
      {readyDocuments.length > 0 && <ClientIntakeDocuments documents={readyDocuments} showTitle={!documentsOnly} onPreview={setAttachmentPreview} onRemove={intake.remove} removalDisabled={saving || intake.busy || !!saveAttempt.current} />}
      </form>
    </Modal>
    {open && attachmentPreview && <DocumentPreviewModal
      open title={categoryLabels[attachmentPreview.category] || "Document"}
      file={{ contentType: attachmentPreview.file.contentType, originalName: categoryLabels[attachmentPreview.category] || "Document",
        downloadURL: `data:${attachmentPreview.file.contentType};base64,${attachmentPreview.file.dataBase64}` }}
      pdfRenderer={PdfDocumentPreview} showOpenInNewTab={false} shellHeight="min(76vh, 720px)"
      style={{ '--doc-preview-stack-height': 'min(76vh, 720px)' }} onClose={() => setAttachmentPreview(null)} />}
    <Dialog visible={open && intake.unidentified} onHide={intake.dismissUnidentified}
      modal blockScroll showHeader={false} draggable={false} resizable={false}
      baseZIndex={10000} className="clientIntakeUnidentified" maskClassName="clientIntakeUnidentifiedMask"
      aria-label="Document not identified">
      <div className="appConfirmCard appConfirm--neutral">
        <div className="appConfirmIcon"><i className="bi bi-exclamation-triangle" aria-hidden="true" /></div>
        <p className="appConfirmText">This document could not be identified. Please upload a passport/national ID, proof of address, CV or trade licence.</p>
        <div className="appConfirmActions">
          <Button label="OK" className="appConfirmBtn appConfirmBtnPrimary" onClick={intake.dismissUnidentified} />
        </div>
      </div>
    </Dialog>
    {open && intake.review && <ClientDocumentIntakeReview review={{ ...intake.review, review: intake.review.review.map(row => ({ ...row, currentValue: ['dateOfBirth', 'nationality', 'incorporationDate'].includes(row.field) ? intake.kyc[row.field]?.value ?? existingKyc[row.field] ?? row.currentValue ?? '' : draft[row.field] || '' })) }} open={intake.reviewOpen && !intake.unidentified} onClose={intake.closeReview} onResolve={intake.resolveReview} />}
    </>
  );
}
